import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import API_URL from '../../config';
import './PricingCalendar.css';

function PricingCalendar() {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState([]);
  const [bulkEdit, setBulkEdit] = useState({ season: '', owner_rate: '', renter_rate: '' });
  const [editingDate, setEditingDate] = useState(null);

  const { getAuthHeader } = useAuth();

  const fetchPricing = useCallback(async () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const startDate = new Date(year, month, 1).toISOString().split('T')[0];
    const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

    try {
      const response = await fetch(
        `${API_URL}/api/calendar-pricing?startDate=${startDate}&endDate=${endDate}`,
        { headers: getAuthHeader() }
      );
      const data = await response.json();
      setPricing(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      setPricing([]);
    }
  }, [currentMonth, getAuthHeader]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchPricing();
      setLoading(false);
    };
    loadData();
  }, [fetchPricing]);

  const handleDateClick = (dateStr) => {
    setSelectedDates(prev =>
      prev.includes(dateStr)
        ? prev.filter(d => d !== dateStr)
        : [...prev, dateStr]
    );
  };

  const handleDateDoubleClick = (dateStr, pricingData) => {
    setEditingDate({
      date: dateStr,
      season: pricingData?.season || '',
      owner_rate: pricingData?.owner_rate || 0,
      renter_rate: pricingData?.renter_rate || 0,
      notes: pricingData?.notes || ''
    });
  };

  const handleSaveDate = async () => {
    try {
      await fetch(`${API_URL}/api/calendar-pricing/${editingDate.date}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(editingDate)
      });
      setEditingDate(null);
      fetchPricing();
    } catch (error) {
      console.error('Error saving date:', error);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedDates.length === 0) {
      alert('Select dates first');
      return;
    }

    const sortedDates = [...selectedDates].sort();
    const startDate = sortedDates[0];
    const endDate = sortedDates[sortedDates.length - 1];

    try {
      await fetch(`${API_URL}/api/calendar-pricing/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({
          startDate,
          endDate,
          season: bulkEdit.season || null,
          owner_rate: bulkEdit.owner_rate ? parseFloat(bulkEdit.owner_rate) : null,
          renter_rate: bulkEdit.renter_rate ? parseFloat(bulkEdit.renter_rate) : null
        })
      });

      setSelectedDates([]);
      setBulkEdit({ season: '', owner_rate: '', renter_rate: '' });
      fetchPricing();
    } catch (error) {
      console.error('Error bulk updating:', error);
    }
  };

  const handleExport = () => {
    window.open(`${API_URL}/api/calendar-pricing/export`, '_blank');
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const data = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',');
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index]?.trim();
          });
          
          if (row.date) {
            data.push({
              date: row.date,
              season: row.season || null,
              owner_rate: parseFloat(row.owner_rate) || 0,
              renter_rate: parseFloat(row.renter_rate) || 0,
              notes: row.notes || null
            });
          }
        }

        const response = await fetch(`${API_URL}/api/calendar-pricing/import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify({ data })
        });

        const result = await response.json();
        if (response.ok) {
          alert(`✅ ${result.message}`);
          fetchPricing();
        } else {
          alert(`❌ Error: ${result.error}`);
        }
      } catch (error) {
        console.error('Error importing CSV:', error);
        alert('❌ Failed to import CSV file');
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  const getPricingForDate = (dateStr) => {
    return pricing.find(p => p.date.split('T')[0] === dateStr);
  };

  const getSeasonColor = (season) => {
    const colors = {
      'Peak': '#e74c3c',
      'Off-Peak': '#27ae60',
      'Holiday': '#9b59b6',
      'Shoulder': '#f39c12',
      'Weekend': '#3498db'
    };
    return colors[season] || '#95a5a6';
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    dayNames.forEach(day => {
      days.push(<div key={`header-${day}`} className="calendar-header-cell">{day}</div>);
    });

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-cell empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const pricingData = getPricingForDate(dateStr);
      const isSelected = selectedDates.includes(dateStr);
      const dayOfWeek = new Date(year, month, day).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      days.push(
        <div
          key={dateStr}
          className={`calendar-cell ${isSelected ? 'selected' : ''} ${pricingData ? 'has-pricing' : ''} ${isWeekend ? 'weekend' : ''}`}
          onClick={() => handleDateClick(dateStr)}
          onDoubleClick={() => handleDateDoubleClick(dateStr, pricingData)}
          style={pricingData?.season ? { borderLeftColor: getSeasonColor(pricingData.season) } : {}}
        >
          <div className="day-number">{day}</div>
          {pricingData && (
            <div className="day-pricing">
              {pricingData.season && (
                <div className="season-tag" style={{ backgroundColor: getSeasonColor(pricingData.season) }}>
                  {pricingData.season}
                </div>
              )}
              <div className="rates">
                <div className="rate owner-rate">O: ${pricingData.owner_rate}</div>
                <div className="rate renter-rate">R: ${pricingData.renter_rate}</div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  if (loading) return <div>Loading pricing data...</div>;

  return (
    <div className="pricing-calendar">
      {/* Edit Single Date Modal */}
      {editingDate && (
        <div className="modal-overlay" onClick={() => setEditingDate(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Edit {editingDate.date}</h3>
            <div className="modal-form">
              <label>
                Season:
                <input
                  value={editingDate.season}
                  onChange={e => setEditingDate({...editingDate, season: e.target.value})}
                  placeholder="e.g., Peak, Off-Peak, Holiday"
                />
              </label>
              <label>
                Owner Rate:
                <input
                  type="number"
                  value={editingDate.owner_rate}
                  onChange={e => setEditingDate({...editingDate, owner_rate: e.target.value})}
                />
              </label>
              <label>
                Renter Rate:
                <input
                  type="number"
                  value={editingDate.renter_rate}
                  onChange={e => setEditingDate({...editingDate, renter_rate: e.target.value})}
                />
              </label>
              <label>
                Notes:
                <input
                  value={editingDate.notes}
                  onChange={e => setEditingDate({...editingDate, notes: e.target.value})}
                />
              </label>
              <div className="modal-actions">
                <button onClick={handleSaveDate}>Save</button>
                <button onClick={() => setEditingDate(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="calendar-controls">
        <div className="month-nav">
          <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>
            &lt; Prev
          </button>
          <select 
            value={currentMonth.getMonth()} 
            onChange={(e) => setCurrentMonth(new Date(currentMonth.getFullYear(), parseInt(e.target.value)))}
            className="month-selector"
          >
            <option value={0}>January</option>
            <option value={1}>February</option>
            <option value={2}>March</option>
            <option value={3}>April</option>
            <option value={4}>May</option>
            <option value={5}>June</option>
            <option value={6}>July</option>
            <option value={7}>August</option>
            <option value={8}>September</option>
            <option value={9}>October</option>
            <option value={10}>November</option>
            <option value={11}>December</option>
          </select>
          <select 
            value={currentMonth.getFullYear()} 
            onChange={(e) => setCurrentMonth(new Date(parseInt(e.target.value), currentMonth.getMonth()))}
            className="year-selector"
          >
            {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>
            Next &gt;
          </button>
        </div>

        <div className="action-buttons">
          <button className="export-btn" onClick={handleExport}>Export CSV</button>
          <label className="import-btn">
            Import CSV
            <input 
              type="file" 
              accept=".csv" 
              onChange={handleImport}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      {/* Bulk Edit Panel */}
      {selectedDates.length > 0 && (
        <div className="bulk-edit-panel">
          <span>{selectedDates.length} dates selected</span>
          <input
            type="text"
            placeholder="Season"
            value={bulkEdit.season}
            onChange={e => setBulkEdit({...bulkEdit, season: e.target.value})}
          />
          <input
            type="number"
            placeholder="Owner Rate"
            value={bulkEdit.owner_rate}
            onChange={e => setBulkEdit({...bulkEdit, owner_rate: e.target.value})}
          />
          <input
            type="number"
            placeholder="Renter Rate"
            value={bulkEdit.renter_rate}
            onChange={e => setBulkEdit({...bulkEdit, renter_rate: e.target.value})}
          />
          <button onClick={handleBulkUpdate}>Apply</button>
          <button onClick={() => setSelectedDates([])}>Clear</button>
        </div>
      )}

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {renderCalendar()}
      </div>

      {/* Instructions */}
      <div className="calendar-instructions">
        <p><strong>Click</strong> to select dates | <strong>Double-click</strong> to edit a single date | <strong>Export CSV</strong> for bulk editing in Excel</p>
      </div>
    </div>
  );
}

export default PricingCalendar;
